import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Plus, Search, KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { useAuth } from '@/lib/auth';

export default function StaffManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', phone: '', role_id: '', scope_mode: 'ALL', state_id: '', branch_id: '' });
  const [loading, setLoading] = useState(false);

  // Password reset modal state
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState({ new_password: '', confirm_password: '', force_change_on_next_login: true, reason: '' });
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetErrors, setResetErrors] = useState([]);

  const hasResetPermission = currentUser?.permissions?.includes('auth.password_reset') || currentUser?.permissions?.includes('auth.manage_users');

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, statesRes, branchesRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/roles'),
        api.get('/org/states'),
        api.get('/org/branches'),
      ]);
      setUsers(usersRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setStates(statesRes.data.data || []);
      setBranches(branchesRes.data.data || []);
    } catch (e) {
      notify.error(e, 'UNKNOWN', 'Failed to load data');
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/admin/users', form);
      toast.success('Staff member created successfully');
      setDialogOpen(false);
      setForm({ email: '', password: '', full_name: '', phone: '', role_id: '', scope_mode: 'ALL', state_id: '', branch_id: '' });
      fetchData();
    } catch (e) {
      notify.error(e, 'UNKNOWN', 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // ── Password Reset Logic ──
  const openResetModal = (user) => {
    setResetTarget(user);
    setResetForm({ new_password: '', confirm_password: '', force_change_on_next_login: true, reason: '' });
    setResetErrors([]);
    setShowPassword(false);
  };

  const validateResetForm = () => {
    const errs = [];
    const p = resetForm.new_password;
    if (p.length < 10) errs.push('At least 10 characters');
    if (!/[A-Z]/.test(p)) errs.push('At least one uppercase letter');
    if (!/[a-z]/.test(p)) errs.push('At least one lowercase letter');
    if (!/[0-9]/.test(p)) errs.push('At least one number');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p)) errs.push('At least one special character');
    if (p !== resetForm.confirm_password) errs.push('Passwords do not match');
    setResetErrors(errs);
    return errs.length === 0;
  };

  const handlePasswordReset = async () => {
    if (!validateResetForm()) return;
    setResetLoading(true);
    try {
      await api.post(`/auth/admin/users/${resetTarget.id}/password/reset`, {
        new_password: resetForm.new_password,
        force_change_on_next_login: resetForm.force_change_on_next_login,
        reason: resetForm.reason || 'Admin reset',
      });
      toast.success(`Password reset for ${resetTarget.full_name}`);
      setResetTarget(null);
    } catch (e) {
      const detail = e.response?.data?.error || e.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (typeof detail === 'string' ? detail : 'Password reset failed');
      notify.error(e, detail?.code || 'UNKNOWN', msg);
    } finally {
      setResetLoading(false);
    }
  };

  const filtered = users.filter((u) => {
    if (roleFilter && u.role_name !== roleFilter) return false;
    if (search && !u.full_name?.toLowerCase().includes(search.toLowerCase()) && !u.email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Staff Management</h1>
          <p className="text-muted-foreground mt-1">Manage your support workers and administrators</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="staff-create-button">
              <Plus className="h-4 w-4 mr-2" /> Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Staff Member</DialogTitle>
              <DialogDescription>Add a new staff member to the system</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} required data-testid="staff-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="staff-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required data-testid="staff-password-input" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={form.role_id} onValueChange={(v) => setForm({...form, role_id: v})}>
                    <SelectTrigger data-testid="staff-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Select value={form.scope_mode} onValueChange={(v) => setForm({...form, scope_mode: v})}>
                    <SelectTrigger data-testid="staff-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      <SelectItem value="STATE">State</SelectItem>
                      <SelectItem value="BRANCH">Branch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={form.state_id} onValueChange={(v) => setForm({...form, state_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>
                      {states.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({...form, branch_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="staff-submit-button">
                {loading ? 'Creating...' : 'Create Staff Member'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="staff-list-search-input" />
          </div>
          <Select value={roleFilter || 'all'} onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[160px]" data-testid="user-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} user(s)</p>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Status</TableHead>
                {hasResetPermission && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={hasResetPermission ? 6 : 5} className="text-center py-8 text-muted-foreground">No staff found</TableCell></TableRow>
              ) : (
                filtered.map((u) => (
                  <TableRow key={u.id} data-testid={`staff-row-${u.id}`} className="hover:bg-muted/60">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                          {u.full_name?.charAt(0)}
                        </div>
                        {u.full_name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>{u.role_name}</TableCell>
                    <TableCell><StatusBadge status={u.scope_mode || 'ALL'} /></TableCell>
                    <TableCell><StatusBadge status={u.is_active ? 'active' : 'inactive'} /></TableCell>
                    {hasResetPermission && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`reset-password-btn-${u.id}`}
                          onClick={() => openResetModal(u)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <KeyRound className="h-4 w-4 mr-1" /> Reset
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Password Reset Modal */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-semibold">{resetTarget?.full_name}</span> ({resetTarget?.email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="reset-new-password"
                  data-testid="reset-new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={resetForm.new_password}
                  onChange={(e) => { setResetForm({...resetForm, new_password: e.target.value}); setResetErrors([]); }}
                  placeholder="Min 10 chars, uppercase, lowercase, number, special"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-confirm-password">Confirm Password</Label>
              <Input
                id="reset-confirm-password"
                data-testid="reset-confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                value={resetForm.confirm_password}
                onChange={(e) => { setResetForm({...resetForm, confirm_password: e.target.value}); setResetErrors([]); }}
                placeholder="Re-enter password"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="force-change"
                data-testid="reset-force-change-checkbox"
                checked={resetForm.force_change_on_next_login}
                onChange={(e) => setResetForm({...resetForm, force_change_on_next_login: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="force-change" className="text-sm font-normal cursor-pointer">
                Force password change on next login
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-reason">Reason for reset</Label>
              <Input
                id="reset-reason"
                data-testid="reset-reason-input"
                value={resetForm.reason}
                onChange={(e) => setResetForm({...resetForm, reason: e.target.value})}
                placeholder="e.g., User forgot password, New staff onboarding"
              />
            </div>

            {resetErrors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 space-y-1" data-testid="reset-validation-errors">
                {resetErrors.map((err, i) => (
                  <p key={i} className="text-sm text-destructive">{err}</p>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetTarget(null)} data-testid="reset-cancel-button">Cancel</Button>
            <Button
              onClick={handlePasswordReset}
              disabled={resetLoading || !resetForm.new_password || !resetForm.confirm_password}
              data-testid="reset-save-button"
            >
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
